// MJ header behaviour: sticky show/hide, menu panels, announcement slider,
// mobile drawer dialog, sidecart + search wiring.
//
// Menu panels: sections/ab-header.liquid ships each panel's contents once, as
// an inert <template> (snippets/ab-header-panels.liquid), so nothing in a
// closed panel is live DOM — or a network request — at load. Desktop: one
// instance per panel, cloned on the first hover / focus / tap of its trigger
// and appended to whichever trigger item opened it (static bar or sticky
// clone), so the two bars share a single panel DOM. Phone: the drawer's
// accordion hosts are filled from the same templates when the drawer opens.
//
// Panel images are warmed in two stages so an opened panel shows them at
// once without competing with the page: after load (idle, low priority) each
// template's images are fetched by detached Image objects carrying the same
// srcset/sizes, so the browser picks the very file the panel will render;
// then intent (a touch on a trigger or drawer heading) starts a panel's
// images ahead of the open. Clones of warmed panels load eagerly — a memory
// cache hit, complete on the first frame.
//
// init() binds everything that lives inside the section; it runs at load and
// again when the theme editor re-renders the section (shopify:section:load).
// The window/document listeners are bound once and act on the current
// instance (`current`).

(function () {
  let current = null;
  const desktopNav = window.matchMedia('(min-width: 1240px)');
  // The drawer shows only the tiles' images (ab-header.css hides Shop All's)
  const DRAWER_IMAGES = '.ab-header__tile-image img';
  const warmedPanels = new Set(); // 'nav:' / 'drawer:' + panel block id
  const warmImages = []; // held so the memory cache keeps them until the panel opens

  // Fetches a panel's images for the current layout (desktop panel or drawer
  // accordion). The Image takes the template image's srcset + sizes, so the
  // candidate is chosen for the rendered tile width and pixel density.
  function warmPanel(id, priority) {
    const desktop = desktopNav.matches;
    const key = (desktop ? 'nav:' : 'drawer:') + id;
    if (warmedPanels.has(key)) return;
    warmedPanels.add(key);
    const template = document.getElementById('HeaderPanelTpl-' + id);
    if (!template) return;
    template.content.querySelectorAll(desktop ? 'img' : DRAWER_IMAGES).forEach(function (source) {
      const image = new Image();
      image.fetchPriority = priority;
      image.decoding = 'async';
      ['sizes', 'srcset', 'src'].forEach(function (name) {
        if (source.hasAttribute(name)) image.setAttribute(name, source.getAttribute(name));
      });
      warmImages.push(image);
    });
  }

  // Stage 1: once the page has loaded and the main thread is idle, every
  // panel at low priority — skipped when the visitor asked to save data or
  // is on a 2G-class connection (intent still warms the panel they reach for).
  function warmAllPanels() {
    const connection = navigator.connection;
    if (connection && (connection.saveData || /2g$/.test(connection.effectiveType))) return;
    document.querySelectorAll('template[id^="HeaderPanelTpl-"]').forEach(function (template) {
      warmPanel(template.id.slice('HeaderPanelTpl-'.length), 'low');
    });
  }

  function scheduleWarmUp() {
    if (window.requestIdleCallback) requestIdleCallback(warmAllPanels, { timeout: 2000 });
    else setTimeout(warmAllPanels, 0);
  }

  function init() {
    const header = document.querySelector('.ab-header');
    if (!header) return;
    const instance = {};
    current = instance;

    // Sticky header: a fixed clone slides in once the page is scrolled past
    // the static header. Absent on pages where the section's sticky mode is
    // off. One boolean is the whole state and every scroll re-derives it from
    // scrollY, so a flip mid-slide just reverses the CSS transition — there is
    // no timer that can land a stale state after the user has already scrolled
    // back (the old animating-lock + setTimeout pair left the clone parked over
    // the static header at the top of the page).
    const stickyHeader = document.querySelector('.ab-header-sticky');
    const STICKY_THRESHOLD = 150;
    let stickyShown = false;
    let scrollTicking = false;

    function setStickyShown(shown) {
      if (shown === stickyShown) return;
      stickyShown = shown;
      stickyHeader.classList.toggle('is-visible', shown);
      stickyHeader.setAttribute('aria-hidden', shown ? 'false' : 'true');
      if (shown) {
        stickyHeader.removeAttribute('inert');
      } else {
        stickyHeader.setAttribute('inert', '');
        if (openItem && stickyHeader.contains(openItem)) closePanel();
      }
    }

    function updateOnScroll() {
      scrollTicking = false;
      if (stickyHeader) setStickyShown(window.scrollY > STICKY_THRESHOLD);
      // Keep an open panel pinned under the bar that owns it
      if (openItem) positionPanel(openItem);
    }

    instance.handleScroll = function () {
      if (scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(updateOnScroll);
    };

    // -------------------------------------------------------------------
    // Menu panels (desktop, ≥1240px — the nav is display:none below that)
    // -------------------------------------------------------------------
    const desktopPanels = new Map(); // panel block id -> its one desktop instance
    let openItem = null;
    let closeTimer = null;

    // `eager`: selector of the images that load eagerly in the clone (the
    // rest keep the template's loading="lazy").
    function clonePanel(id, eager) {
      const template = document.getElementById('HeaderPanelTpl-' + id);
      if (!template) return null;
      const panel = template.content.firstElementChild.cloneNode(true);
      if (eager) panel.querySelectorAll(eager).forEach(function (img) { img.loading = 'eager'; });
      return panel;
    }

    // A desktop clone is made to be shown at once, so its images load
    // eagerly: in the same task as the hover, not after a lazy-load pass.
    function panelFor(item) {
      const id = item.querySelector('[data-panel]').dataset.panel;
      if (!desktopPanels.has(id)) desktopPanels.set(id, clonePanel(id, 'img'));
      return desktopPanels.get(id);
    }

    function positionPanel(item) {
      const panel = item.querySelector('.ab-header__panel');
      if (!panel) return;
      const bar = item.closest('.ab-header');
      panel.style.setProperty('--ab-header-panel-top', bar.getBoundingClientRect().bottom + 'px');
    }

    function openPanel(item) {
      clearTimeout(closeTimer);
      if (openItem === item) return;
      const panel = panelFor(item);
      if (!panel) return;
      closePanel();
      if (panel.parentElement !== item) item.appendChild(panel);
      openItem = item;
      positionPanel(item);
      panel.classList.add('is-open');
      const trigger = item.querySelector('[data-panel]');
      trigger.setAttribute('aria-expanded', 'true');
      trigger.setAttribute('aria-controls', panel.id);
      item.closest('.ab-header').setAttribute('data-dropdown-open', '');
    }

    function closePanel() {
      clearTimeout(closeTimer);
      if (!openItem) return;
      const item = openItem;
      openItem = null;
      const panel = item.querySelector('.ab-header__panel');
      if (panel) panel.classList.remove('is-open');
      item.querySelector('[data-panel]').setAttribute('aria-expanded', 'false');
      item.closest('.ab-header').removeAttribute('data-dropdown-open');
    }

    // The item spans the bar's full height (ab-header.css), so the pointer
    // stays inside it on the way down into the panel; the grace period only
    // covers a brief excursion.
    function scheduleClose() {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(closePanel, 120);
    }

    instance.closeIfOutside = function (target) {
      if (openItem && !openItem.contains(target)) closePanel();
    };
    instance.openPanel = openPanel;

    document.querySelectorAll('.ab-header__nav-item--panel').forEach(function (item) {
      const trigger = item.querySelector('[data-panel]');
      item.addEventListener('mouseenter', function () { openPanel(item); });
      item.addEventListener('mouseleave', scheduleClose);
      // Keyboard: the panel opens on focus and sits right after the trigger
      // in the DOM, so Tab walks its tiles; it closes when focus leaves the
      // item. Guarded on openItem: when another panel opens and hides this
      // one, the browser's deferred focus loss must not close the panel that
      // replaced it.
      item.addEventListener('focusin', function () { openPanel(item); });
      item.addEventListener('focusout', function (event) {
        if (openItem === item && !item.contains(event.relatedTarget)) closePanel();
      });
      // Escape: focus the trigger first (its focusin is a no-op while the
      // panel is open), then close.
      item.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && openItem === item) {
          event.preventDefault();
          trigger.focus();
          closePanel();
        }
      });
      // Touch: the first tap opens the panel instead of following the
      // trigger's link; a tap on the trigger of an open panel follows it.
      // The touch starting is the intent: its images start before the click.
      trigger.addEventListener('pointerdown', function (event) {
        if (event.pointerType !== 'touch') return;
        item.dataset.tapOpens = String(openItem !== item);
        warmPanel(trigger.dataset.panel, 'high');
      });
      trigger.addEventListener('click', function (event) {
        if (item.dataset.tapOpens === 'true') {
          event.preventDefault();
          openPanel(item);
        }
        delete item.dataset.tapOpens;
      });
    });

    updateOnScroll();
    measureScrollbar();

    // -------------------------------------------------------------------
    // Announcement bar slider: cycle through slides + chevron navigation
    // when there are 2+ blocks
    // -------------------------------------------------------------------
    const announcementSlider = document.querySelector('[data-announcement-slider].ab-header__announcement--has-multiple');
    if (announcementSlider) {
      const slides = Array.from(announcementSlider.querySelectorAll('.ab-header__announcement-slide'));
      const prevBtn = announcementSlider.querySelector('[data-announcement-prev]');
      const nextBtn = announcementSlider.querySelector('[data-announcement-next]');

      if (slides.length > 1) {
        const intervalSec = parseFloat(announcementSlider.dataset.interval) || 5;
        const intervalMs = Math.max(2000, intervalSec * 1000);
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const TRANSITION_MS = prefersReducedMotion ? 200 : 600;

        let currentIndex = 0;
        let isTransitioning = false;
        let sliderTimer = null;
        let manual = false;

        function goToSlide(targetIndex, direction) {
          if (isTransitioning || targetIndex === currentIndex) return;
          isTransitioning = true;

          const currentSlide = slides[currentIndex];
          const next = slides[targetIndex];
          const fromX = prefersReducedMotion ? '0' : (direction === 'next' ? '100%' : '-100%');
          const toX = prefersReducedMotion ? '0' : (direction === 'next' ? '-100%' : '100%');

          // Park next slide off-screen on the entering side without animating the jump
          next.style.transition = 'none';
          next.style.transform = 'translateX(' + fromX + ')';
          next.style.opacity = '0';
          void next.offsetWidth; // force reflow so the parked position takes effect
          next.style.transition = '';

          // Current slide animates out to opposite side
          currentSlide.classList.remove('is-active');
          currentSlide.style.transform = 'translateX(' + toX + ')';
          currentSlide.style.opacity = '0';

          // Next slide animates into view — clear inline transform so the CSS rules win
          next.style.transform = '';
          next.style.opacity = '';
          next.classList.add('is-active');

          currentIndex = targetIndex;

          setTimeout(function () {
            currentSlide.style.transform = '';
            currentSlide.style.opacity = '';
            currentSlide.style.transition = '';
            isTransitioning = false;
          }, TRANSITION_MS + 50);
        }

        function nextSlide() {
          goToSlide((currentIndex + 1) % slides.length, 'next');
        }
        function prevSlide() {
          goToSlide((currentIndex - 1 + slides.length) % slides.length, 'prev');
        }

        function startTimer() {
          stopTimer();
          sliderTimer = setInterval(nextSlide, intervalMs);
        }
        function stopTimer() {
          if (sliderTimer) {
            clearInterval(sliderTimer);
            sliderTimer = null;
          }
        }

        // Only the visible offer is exposed to assistive tech
        function syncHidden() {
          slides.forEach(function (slide, i) {
            slide.setAttribute('aria-hidden', i === currentIndex ? 'false' : 'true');
          });
        }
        syncHidden();
        const syncOnActive = new MutationObserver(syncHidden);
        slides.forEach(function (slide) { syncOnActive.observe(slide, { attributes: true, attributeFilter: ['class'] }); });

        // Manual navigation hands control to the visitor: the auto-cycle stops for good
        if (prevBtn) {
          prevBtn.addEventListener('click', function () { stopTimer(); prevSlide(); });
        }
        if (nextBtn) {
          nextBtn.addEventListener('click', function () { stopTimer(); nextSlide(); });
        }

        // Hover / focus pause the cycle
        announcementSlider.addEventListener('mouseenter', stopTimer);
        announcementSlider.addEventListener('mouseleave', function () { if (!manual) startTimer(); });
        announcementSlider.addEventListener('focusin', stopTimer);
        announcementSlider.addEventListener('focusout', function () { if (!manual) startTimer(); });
        announcementSlider.addEventListener('click', function () { manual = true; });

        if (!prefersReducedMotion) startTimer();
      }
    }

    // -------------------------------------------------------------------
    // Mobile drawer: a modal dialog. Closed, it is inert (not just translated
    // off-screen) so its links are not Tab stops behind every phone page;
    // open, focus moves to the close button, Tab cycles inside it (tabbables
    // are read per keypress — the accordions change what is focusable),
    // Escape and the scrim close it, and focus returns to the hamburger that
    // opened it.
    // -------------------------------------------------------------------
    const mobileToggles = document.querySelectorAll('.ab-header__mobile-toggle');
    const mobileDrawer = document.querySelector('[data-mobile-drawer]');
    const mobileOverlay = document.querySelector('[data-mobile-overlay]');
    const mobileClose = document.querySelector('.ab-header__mobile-drawer-close');
    let drawerOpener = null;
    let drawerFilled = false;

    // The accordion bodies are empty until the drawer first opens; then each
    // is filled from its panel's template (the desktop instances are separate).
    // Warmed panels' tiles load eagerly (cached, so complete when the
    // accordion opens); the others stay lazy until their heading is touched.
    function fillDrawerPanels() {
      if (drawerFilled) return;
      drawerFilled = true;
      mobileDrawer.querySelectorAll('[data-panel-host]').forEach(function (host) {
        const id = host.dataset.panelHost;
        const panel = clonePanel(id, warmedPanels.has('drawer:' + id) ? DRAWER_IMAGES : null);
        if (!panel) return;
        panel.removeAttribute('id');
        host.appendChild(panel);
      });
    }

    function onDrawerKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMobileDrawer();
        return;
      }
      if (event.key !== 'Tab') return;
      const tabbables = getFocusableElements(mobileDrawer).filter((element) => element.offsetParent !== null);
      const first = tabbables[0];
      const last = tabbables[tabbables.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === mobileDrawer)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function openMobileDrawer(event) {
      if (mobileDrawer.classList.contains('is-open')) return;
      document.querySelector('ab-search')?.close();
      fillDrawerPanels();
      drawerOpener = event && event.currentTarget instanceof HTMLElement ? event.currentTarget : mobileToggles[0];
      mobileDrawer.removeAttribute('inert');
      mobileDrawer.classList.add('is-open');
      mobileOverlay.classList.add('is-open');
      mobileToggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'true'));
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', onDrawerKeydown);
      desktopNav.addEventListener('change', closeMobileDrawer);
      mobileClose.focus({ preventScroll: true });
    }

    // Returns the hamburger focus goes back to (the static one when the sticky
    // clone that opened the drawer has since been hidden).
    function closeMobileDrawer() {
      if (!mobileDrawer.classList.contains('is-open')) return drawerOpener;
      mobileDrawer.classList.remove('is-open');
      mobileOverlay.classList.remove('is-open');
      mobileDrawer.setAttribute('inert', '');
      mobileToggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', onDrawerKeydown);
      desktopNav.removeEventListener('change', closeMobileDrawer);
      const target = drawerOpener && !drawerOpener.closest('[inert]') ? drawerOpener : mobileToggles[0];
      if (mobileDrawer.contains(document.activeElement)) target.focus({ preventScroll: true });
      return target;
    }

    if (mobileDrawer && mobileOverlay && mobileClose) {
      mobileToggles.forEach(function (toggle) {
        toggle.addEventListener('click', openMobileDrawer);
      });
      mobileClose.addEventListener('click', closeMobileDrawer);
      mobileOverlay.addEventListener('click', closeMobileDrawer);

      // Accordion: one panel open at a time (mj-disclosure eases the reveal).
      // When the one closing sits above the one tapped, the tapped heading is
      // held where the finger was while the content above eases shut —
      // Chrome's scroll anchoring picks a node inside the shrinking box and
      // lets the heading fly off the top otherwise.
      const drawerContent = mobileDrawer.querySelector('.ab-header__mobile-drawer-content');
      const drawerDetails = mobileDrawer.querySelectorAll('.ab-header__mobile-drawer-details');
      drawerDetails.forEach(function (details) {
        // Intent: a touch (or focus) on the heading starts its tiles' images
        // before the click toggles the accordion open.
        const summary = details.querySelector('summary');
        function startImages() {
          details.querySelectorAll(DRAWER_IMAGES).forEach(function (img) { img.loading = 'eager'; });
        }
        summary.addEventListener('pointerdown', startImages);
        summary.addEventListener('focus', startImages);
        details.addEventListener('toggle', function () {
          if (!details.open) return;
          const heldTop = summary.getBoundingClientRect().top;
          let closedAbove = false;
          drawerDetails.forEach(function (other) {
            if (other === details || !other.open) return;
            other.open = false;
            if (other.compareDocumentPosition(details) & Node.DOCUMENT_POSITION_FOLLOWING) closedAbove = true;
          });
          if (!closedAbove) return;
          const until = performance.now() + 450;
          (function hold() {
            drawerContent.scrollTop += summary.getBoundingClientRect().top - heldTop;
            if (performance.now() < until) requestAnimationFrame(hold);
          })();
        });
      });
    }

    // Sidecart wiring: every header cart link opens the drawer (cart-drawer.js
    // only binds #cart-icon-bubble); from the menu, the menu closes first and
    // the sidecart hands focus back to the hamburger. Secondary count badges
    // follow the re-rendered primary one after cart updates.
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      document.querySelectorAll('[data-cart-link]:not(#cart-icon-bubble)').forEach((link) => {
        link.setAttribute('role', 'button');
        link.setAttribute('aria-haspopup', 'dialog');
        link.addEventListener('click', (event) => {
          event.preventDefault();
          const fromDrawer = mobileDrawer && mobileDrawer.contains(link);
          cartDrawer.open(fromDrawer ? closeMobileDrawer() : link);
        });
      });

      const primary = document.getElementById('cart-icon-bubble');
      if (primary) {
        const sync = () => {
          const source = primary.querySelector('.cart-count-badge');
          if (!source) return;
          document.querySelectorAll('[data-cart-link]:not(#cart-icon-bubble) .cart-count-badge').forEach((badge) => {
            badge.textContent = source.textContent;
            badge.classList.toggle('hidden', source.classList.contains('hidden'));
          });
        };
        new MutationObserver(sync).observe(primary, { childList: true, subtree: true, characterData: true });
      }
    }

    // Search buttons (static header, sticky clone, mobile drawer) toggle the
    // search panel (<ab-search>, assets/ab-search.js). From the drawer, the
    // drawer closes first and focus later returns to the hamburger — the
    // drawer's own button is inert by then.
    const search = document.querySelector('ab-search');
    if (search) {
      document.querySelectorAll('.ab-header__action-button--search, .ab-header__mobile-drawer-action--search').forEach(function (button) {
        button.addEventListener('click', function () {
          const fromDrawer = mobileDrawer && mobileDrawer.contains(button);
          search.toggle(fromDrawer ? closeMobileDrawer() : button);
        });
      });

      // A tap that landed before this script ran (inline listener in sections/ab-header.liquid)
      const early = window.abSearchEarly;
      if (early) {
        document.removeEventListener('click', early);
        window.abSearchEarly = null;
        if (early.button && early.button.isConnected) search.open(early.button);
      }
    }
  }

  // Width of the page's classic scrollbar (0 for overlay scrollbars), read
  // while the page can scroll; body.overflow-hidden pads it back so a locked
  // page does not re-centre (ab-tokens.css).
  function measureScrollbar() {
    if (document.body.classList.contains('overflow-hidden')) return;
    const width = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--mj-scrollbar', width + 'px');
  }

  // Bound once; act on the current instance.
  window.addEventListener('scroll', function () { if (current) current.handleScroll(); }, { passive: true });
  window.addEventListener('pageshow', function () { if (current) current.handleScroll(); });
  window.addEventListener('resize', measureScrollbar);
  document.addEventListener('pointerdown', function (event) { if (current) current.closeIfOutside(event.target); });
  // Enter in a sidecart quantity field must commit the value, not implicitly
  // submit #CartDrawer-Form (whose default submit button is Checkout)
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Enter' && event.target.matches('#CartDrawer-Form .quantity__input')) {
      event.preventDefault();
      event.target.blur();
    }
  });

  // Theme editor: re-bind after the section re-renders; selecting a Menu
  // panel block opens its panel. (Only the editor dispatches these events.)
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector('.ab-header')) init();
  });
  document.addEventListener('shopify:block:select', function (event) {
    const item = event.target.closest('.ab-header__nav-item--panel');
    if (item && current) current.openPanel(item);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  if (document.readyState === 'complete') scheduleWarmUp();
  else window.addEventListener('load', scheduleWarmUp, { once: true });
})();
